import React, { Fragment, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Theme,
    Card,
    CardContent,
    Grid,
    Button,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { CapitalizeNames } from 'src/utils/capitalize';
import { makeStyles } from '@material-ui/styles';
import { ILeadInformationCard } from './types';
import {
    Phone,
    User,
    Mail,
    Star,
    Home,
    Truck,
    ArrowRight,
    Airplay,
    UserX,
} from 'react-feather';
import NumberFormat from 'react-number-format';
import {
    AREA_CODES,
    ECountryCode,
} from 'src/modules/forms/fields/AreaCodeTextField/components/AreaCodeSelect';
import { isPermissionGranted } from 'src/utils/permissions/isPermissionGranted';
import { REJECT_LEAD_AUTH, UPDATE_PERMISSION } from 'src/constants/permissions';
import useAuth from 'src/hooks/useAuth';
import useLead from 'src/hooks/useLead';
import { RejectModal } from 'src/components/Lead/Reject/components/RejectModal';
import { STATUSES } from 'src/constants/mongo/status';
import useSubstatus from 'src/hooks/useSubstatus';
import useComment from 'src/hooks/useComment';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        height: '100%',
    },
    icon: {
        width: 16,
        height: 16,
        color: theme.palette.text.secondary,
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    button: {
        height: 56,
    },
    rejectLead: {
        fontSize: 14,
        height: 35,
        whiteSpace: 'nowrap',
        color: theme.palette.error.main,
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
}));

export const LeadInformationCard = ({
    lead,
    store,
    canBeRejected,
    taskId,
    setOpen,
}: ILeadInformationCard) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const { user } = useAuth();
    const { rejectLeadV2 } = useLead();
    const [currentSubstatus, setCurrentSubstatus] = useState('');
    const [rejectComment, setRejectComment] = useState('');
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const { getSubstatuses } = useSubstatus();
    const { clearComment } = useComment();

    const {
        name,
        phone,
        email,
        countryCode,
        source,
        vehicle,
        version,
        status,
        substatus,
    } = lead;

    const { name: storeName } = store;
    const { name: statusName } = status;
    const { name: substatusName } = substatus;

    const selectedCountryCode: ECountryCode =
        AREA_CODES?.[countryCode]?.countryCode || AREA_CODES.MX.countryCode;

    const canReject = isPermissionGranted({
        permissions: user?.permissions || {},
        permission: REJECT_LEAD_AUTH,
        crud: UPDATE_PERMISSION,
    });

    const handleRejectLead = () => {
        rejectLeadV2({
            lead: lead._id,
            comment: rejectComment,
            substatus: currentSubstatus,
            status: STATUSES.REJECTED.ID,
            isTaskFromCalendar: true,
            oldTask: taskId,
        });
        handleClear();
    };

    const handleOpen = () => setRejectModalOpen(true);

    const handleClear = () => {
        clearComment();
        setRejectModalOpen(false);
        setOpen(false);
    };

    useEffect(() => {
        getSubstatuses();
    }, [rejectModalOpen]);

    return (
        <Fragment>
            <RejectModal
                open={rejectModalOpen}
                setOpen={setRejectModalOpen}
                comment={rejectComment}
                setComment={setRejectComment}
                handleReject={handleRejectLead}
                currentSubstatus={currentSubstatus}
                setCurrentSubstatus={setCurrentSubstatus}
            />
            <Card className={classes.root} elevation={0}>
                <CardContent className={classes.content}>
                    <Box className={classes.header}>
                        <Typography variant="h4" color="textPrimary">
                            {t('Leads.Info2')}
                        </Typography>
                        {(canReject || canBeRejected) && (
                            <Button
                                startIcon={<UserX />}
                                onClick={handleOpen}
                                variant="text"
                                className={classes.rejectLead}
                            >
                                {t('Buttons.Reject')}
                            </Button>
                        )}
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6} className={classes.content}>
                            <Box display="flex" alignItems="center" gridGap={5}>
                                <User className={classes.icon} />
                                <Typography
                                    variant="caption"
                                    color="textPrimary"
                                >
                                    {CapitalizeNames(name)}
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gridGap={5}>
                                <Phone className={classes.icon} />
                                <Typography
                                    variant="caption"
                                    color="textPrimary"
                                >
                                    <NumberFormat
                                        isNumericString
                                        value={phone?.replace(
                                            AREA_CODES?.[selectedCountryCode]
                                                ?.code || '',
                                            ''
                                        )}
                                        format={
                                            AREA_CODES?.[selectedCountryCode]
                                                ?.format
                                        }
                                        displayType="text"
                                    />
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gridGap={5}>
                                <Mail className={classes.icon} />
                                <Typography
                                    variant="caption"
                                    color="textPrimary"
                                >
                                    {email}
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gridGap={5}>
                                <Star className={classes.icon} />
                                <Typography
                                    variant="caption"
                                    color="textPrimary"
                                >
                                    {t(`Status.${statusName}`)}
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={6} className={classes.content}>
                            <Box display="flex" alignItems="center" gridGap={5}>
                                <Home className={classes.icon} />
                                <Typography
                                    variant="caption"
                                    color="textPrimary"
                                >
                                    {CapitalizeNames(storeName)}
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gridGap={5}>
                                <Truck className={classes.icon} />
                                <Typography
                                    variant="caption"
                                    color="textPrimary"
                                >
                                    {(typeof vehicle === 'object' &&
                                        vehicle?.model) ||
                                        ''}
                                    {typeof version === 'object' &&
                                        version?.name &&
                                        ' - ' + version?.name}
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gridGap={5}>
                                <Airplay className={classes.icon} />
                                <Typography
                                    variant="caption"
                                    color="textPrimary"
                                >
                                    {(typeof source === 'object' &&
                                        CapitalizeNames(source?.name)) ||
                                        ''}
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gridGap={5}>
                                <Star className={classes.icon} />

                                <Typography
                                    variant="caption"
                                    color="textPrimary"
                                >
                                    {t(`Substatus.${substatusName}`)}
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                endIcon={<ArrowRight />}
                                className={classes.button}
                                href={`/management/leads/${lead._id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {t('Buttons.GoLead')}
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </Fragment>
    );
};
